import { useState } from 'react';
import { Button, Table, Card, Badge, Modal } from 'react-bootstrap';
import { FaEdit, FaBuilding } from 'react-icons/fa';
import { FaCheck } from 'react-icons/fa6';
import { FaX } from 'react-icons/fa6';

import Loader from '../../components/layout/Loader';
import Message from '../../components/layout/Message';
import Meta from '../../components/layout/Meta';
import { useGetCompaniesQuery } from '../../slices/companyApiSlice';
import useIsMobile from '../../utils/useIsMobile';
import CompanyEditScreen from './CompanyEditScreen';

const CompanyListScreen = () => {
    const isMobile = useIsMobile();
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedCompanyRuc, setSelectedCompanyRuc] = useState(null);

    const {
        data: companies,
        isLoading,
        error,
        refetch,
    } = useGetCompaniesQuery();

    const handleEdit = (ruc) => {
        setSelectedCompanyRuc(ruc);
        setShowEditModal(true);
    };

    const handleCloseModal = () => {
        setShowEditModal(false);
        setSelectedCompanyRuc(null);
    };

    return (
        <>
            <Meta title="Lista de Empresas" />
            <div className="admin-header">
                <h1 className="admin-title responsive-title">
                    <FaBuilding className="title-icon" /> Empresas
                    {companies?.length > 0 && (
                        <Badge bg="secondary" className="ms-2">
                            {companies.length}
                        </Badge>
                    )}
                </h1>
            </div>

            {isLoading ? (
                <Loader />
            ) : error ? (
                <Message variant="danger">
                    {error?.data?.message || error?.error || 'Ocurrió un error inesperado'}
                </Message>
            ) : (
                <>
                    {!isMobile && (
                        <div className="admin-table-container shadow-sm mb-4">
                            <Table hover responsive className="admin-table m-0">
                                <thead>
                                    <tr>
                                        <th>RUC</th>
                                        <th className="text-start">Nombre Comercial</th>
                                        <th className="text-start">Razón Social</th>
                                        <th>Ciudad</th>
                                        <th>Activo</th>
                                        <th className="text-center">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {companies?.map((company) => (
                                        <tr key={company.ruc}>
                                            <td>
                                                <Badge bg="light" text="dark">
                                                    {company.ruc}
                                                </Badge>
                                            </td>
                                            <td className="text-start fw-bold">
                                                {company.tradeName}
                                            </td>
                                            <td className="text-start">
                                                {company.legalName}
                                            </td>
                                            <td>{company.city}</td>
                                            <td>
                                                {company.isActive ? (
                                                    <FaCheck className="text-success" />
                                                ) : (
                                                    <FaX className="text-danger" />
                                                )}
                                            </td>
                                            <td className="text-center align-middle">
                                                <div className="action-buttons justify-content-center">
                                                    <Button
                                                        variant="outline-primary"
                                                        className="btn-sm"
                                                        onClick={() => handleEdit(company.ruc)}
                                                        title="Editar"
                                                    >
                                                        <FaEdit />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {isMobile && (
                        <div className="company-cards-container">
                            {companies?.map((company) => (
                                <Card key={company.ruc} className="mb-3 shadow-sm border-0">
                                    <Card.Body className="p-3">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div>
                                                <div className="fw-bold fs-5">{company.tradeName}</div>
                                                <div className="text-muted small">{company.ruc}</div>
                                            </div>
                                            <Button
                                                variant="light"
                                                className="btn-sm border"
                                                onClick={() => handleEdit(company.ruc)}
                                            >
                                                <FaEdit />
                                            </Button>
                                        </div>
                                        
                                        <div className="mb-2 small">
                                            <strong>Razón Social:</strong> {company.legalName}
                                        </div>
                                        <div className="mb-2 small">
                                            <strong>Ciudad:</strong> {company.city}
                                        </div>

                                        <div className="mt-2 text-center small bg-light p-1 rounded">
                                            <span className="text-muted me-2">Estado:</span>
                                            {company.isActive ? (
                                                <Badge bg="success">Activo</Badge>
                                            ) : (
                                                <Badge bg="danger">Inactivo</Badge>
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    )}
                </>
            )}

            <Modal show={showEditModal} onHide={handleCloseModal} size="lg" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>Editar Empresa</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCompanyRuc && (
                        <CompanyEditScreen
                            ruc={selectedCompanyRuc}
                            onClose={handleCloseModal}
                            refetch={refetch}
                        />
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default CompanyListScreen;